import type {
	CourseAccreditation,
	CourseAccreditationByProfession,
} from "@brookbush/types";

import clsx from "clsx";
import { ChevronsUpDownIcon } from "lucide-react";
import { parseAsString, useQueryState } from "next-usequerystate";
import { useMemo } from "react";

import { FORM_SELECT_CHEVRON_CLASS_NAME } from "~/lib/profession/formSelectChevronClassName";

interface CoursesAccreditationSelectProps {
	accreditationsByProfession: CourseAccreditationByProfession[];
}

const CoursesAccreditationSelect = ({
	accreditationsByProfession,
}: CoursesAccreditationSelectProps) => {
	const [accreditationFilter, setAccreditationFilter] = useQueryState(
		"accreditation",
		parseAsString,
	);

	const flattenedAccreditations = useMemo(
		() =>
			accreditationsByProfession.reduce(
				(acc: CourseAccreditation[], curr) =>
					acc.concat(...curr.accreditations),
				[],
			),
		[accreditationsByProfession],
	);

	const currentAccreditation = flattenedAccreditations.find(
		(acc) => acc.slug === accreditationFilter,
	);

	return (
		<div className="relative">
			<select
				className={clsx(
					"w-full cursor-pointer appearance-none rounded-lg border px-3 py-2 shadow shadow-slate-100 dark:bg-slate-700 dark:shadow-slate-900",
				)}
				name="accreditation-filter"
				id="accreditation-filter"
				value={currentAccreditation?.slug}
				onChange={(e) => {
					setAccreditationFilter(e.target.value.length ? e.target.value : null);
				}}
			>
				<option value={""} disabled={!currentAccreditation?.slug} selected>
					{currentAccreditation ? "Clear Filter" : `Filter by accreditation`}
				</option>

				{accreditationsByProfession
					.filter(
						(item) => !item.accreditations.every((accr) => !accr.coursesCount),
					)
					?.map((option) => (
						<>
							<option key={option.slug} disabled value="">
								{option.title}
							</option>

							{option.accreditations
								.filter((accr) => accr.coursesCount)
								.map((accreditation) => (
									<option key={accreditation.slug} value={accreditation.slug}>
										{accreditation.abbreviation}
									</option>
								))}
						</>
					))}
			</select>

			<ChevronsUpDownIcon
				size={17}
				className={FORM_SELECT_CHEVRON_CLASS_NAME}
				aria-hidden
			/>
		</div>
	);
};

export default CoursesAccreditationSelect;
